import axios from 'axios';
import { t, t as i18nT } from '../i18n';

/** Fallback workflow (legacy tasks with no project). Real projects carry their own columns. */
export const DEFAULT_COLUMNS = [
  { key: 'TO DO', name: 'To do', category: 'todo' },
  { key: 'IN PROGRESS', name: 'In progress', category: 'progress' },
  { key: 'FOR REVIEW', name: 'In review', category: 'progress' },
  { key: 'DONE', name: 'Done', category: 'done' },
];
export const STATUSES = DEFAULT_COLUMNS.map((c) => ({ key: c.key, label: t(c.name), color: { todo: '', progress: 'blue', done: 'green' }[c.category] }));

export const CATEGORY_COLOR = { todo: '', progress: 'blue', done: 'green' };

export const PRIORITIES = [
  { key: 'Highest', color: '#c9372c', icon: '⏫' },
  { key: 'High', color: '#e56910', icon: '🔼' },
  { key: 'Medium', color: '#b38600', icon: '🟰' },
  { key: 'Low', color: '#1f845a', icon: '🔽' },
  { key: 'Lowest', color: '#0c66e4', icon: '⏬' },
];

export const ISSUE_TYPES = [
  { key: 'Task', color: '#4bade8', glyph: '✓' },
  { key: 'Bug', color: '#e5493a', glyph: '●' },
  { key: 'Story', color: '#63ba3c', glyph: '▲' },
  { key: 'Epic', color: '#904ee2', glyph: '⚡' },
  { key: 'Subtask', color: '#4bade8', glyph: '↳' },
];

export const LINK_TYPES = ['blocks', 'is blocked by', 'relates to', 'duplicates'];

export const statusMeta = (key, columns = DEFAULT_COLUMNS) => {
  const c = columns.find((x) => x.key === key) || columns[0];
  return { key: c.key, label: t(c.name), category: c.category, color: CATEGORY_COLOR[c.category] };
};
/** enum value (stored in English: 'High', 'Bug', 'To do'...) -> text for the active language */
export const enumLabel = (key) => t(key);
export const priorityMeta = (key) => PRIORITIES.find((p) => p.key === key) || PRIORITIES[2];
export const typeMeta = (key) => ISSUE_TYPES.find((t) => t.key === key) || ISSUE_TYPES[0];
export const isDone = (task, columns = DEFAULT_COLUMNS) => statusMeta(task.statux, columns).category === 'done';

export const issueKey = (t) => t.key || `TASK-${t.taskNum}`;

const num = (v) => (Array.isArray(v) ? v[0] : v);

/** Normalises documents from any endpoint (also strips the legacy trailing helper arrays). */
export const cleanTasks = (raw) =>
  (Array.isArray(raw) ? raw : [])
    .filter((t) => t && typeof t === 'object' && !Array.isArray(t) && t._id)
    .map((t) => ({
      priority: 'Medium',
      issueType: 'Task',
      storyPoints: 0,
      comments: [],
      subtasks: [],
      activity: [],
      tags: [],
      components: [],
      worklogs: [],
      watchers: [],
      links: [],
      attachments: [],
      rank: 0,
      sprintId: null,
      ...t,
      taskNum: num(t.taskNum),
    }));

export async function fetchTasks(address, projectId) {
  const { data } = await axios.get(`${address}/api/tasks`, projectId ? { params: { projectId } } : undefined);
  return cleanTasks(data);
}

export const initialsOf = (name = '') => {
  const p = name.trim().split(/\s+/);
  return `${(p[0] || '?')[0]}${p[1] ? p[1][0] : (p[0] || '  ')[1] || ''}`.toUpperCase();
};

export const isOverdue = (task, columns) =>
  !!task.dueDate && !isDone(task, columns) && new Date(task.dueDate) < new Date(new Date().toDateString());

export const nextTaskNum = (tasks) =>
  String(tasks.reduce((m, t) => Math.max(m, parseInt(t.taskNum, 10) || 0), 0) + 1);

/** "1w 2d 3h 30m" -> minutes (8h day, 5d week, like Jira). Plain numbers are minutes. */
export const parseDuration = (text) => {
  const s = String(text || '').trim().toLowerCase();
  if (!s) return 0;
  if (/^\d+$/.test(s)) return Number(s);
  let total = 0;
  let matched = false;
  s.replace(/(\d+(?:\.\d+)?)\s*(w|d|h|m)/g, (_, n, u) => {
    matched = true;
    total += Number(n) * { w: 5 * 8 * 60, d: 8 * 60, h: 60, m: 1 }[u];
    return '';
  });
  return matched ? Math.round(total) : NaN;
};
export const formatDuration = (min) => {
  if (!min) return '0m';
  const w = Math.floor(min / 2400);
  const d = Math.floor((min % 2400) / 480);
  const h = Math.floor((min % 480) / 60);
  const m = Math.round(min % 60);
  return [w && `${w}w`, d && `${d}d`, h && `${h}h`, m && `${m}m`].filter(Boolean).join(' ') || '0m';
};

/** Rank that puts an item between two neighbours (either may be missing). */
export const rankBetween = (before, after) => {
  if (before == null && after == null) return 1000;
  if (before == null) return after - 1000;
  if (after == null) return before + 1000;
  return (before + after) / 2;
};

// ---------------------------------------------------------------- JQL-lite
const tokenize = (q) => {
  const out = [];
  const re = /\s*("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|!=|<=|>=|~|=|<|>|\(|\)|,|[^\s()=!<>~,]+)/g;
  let m;
  while ((m = re.exec(q)) !== null) if (m[1]) out.push(m[1]);
  return out;
};

const FIELD_GET = {
  assignee: (t) => (t.assigneeToID === 'UnassignedID' || !t.assigneeToID ? '' : t.assigneeTo),
  reporter: (t) => t.author,
  status: (t) => t.statux,
  priority: (t) => t.priority,
  type: (t) => t.issueType,
  issuetype: (t) => t.issueType,
  label: (t) => t.tags || [],
  labels: (t) => t.tags || [],
  component: (t) => t.components || [],
  sprint: (t) => t.__sprintName || '',
  epic: (t) => t.__epicKey || '',
  key: (t) => issueKey(t),
  summary: (t) => t.summary,
  text: (t) => `${t.summary} ${t.description || ''} ${issueKey(t)}`,
  description: (t) => t.description || '',
  fixversion: (t) => t.fixVersion || '',
  created: (t) => t.createdAt,
  updated: (t) => t.updatedAt,
  due: (t) => t.dueDate,
  resolved: (t) => t.resolvedAt,
  points: (t) => t.storyPoints || 0,
  category: (t) => t.__category || '',
  flagged: (t) => (t.flagged ? 'true' : 'false'),
};

const relDate = (v) => {
  const m = /^([+-]?\d+)([dwm])$/.exec(v);
  if (!m) return v === 'now()' ? Date.now() : v === 'startofday()' ? new Date(new Date().toDateString()).getTime() : null;
  const days = Number(m[1]) * { d: 1, w: 7, m: 30 }[m[2]];
  return Date.now() + days * 86400000;
};

/**
 * Parses e.g.  assignee = currentUser() AND status != Done AND (priority in (High, Highest) OR label = ui) ORDER BY updated DESC
 * Returns { test(task), order:[{field,dir}] } or throws Error(message).
 */
export function parseJql(query, ctx = {}) {
  const orderIdx = query.search(/\border\s+by\b/i);
  const where = orderIdx === -1 ? query : query.slice(0, orderIdx);
  const orderText = orderIdx === -1 ? '' : query.slice(orderIdx).replace(/^order\s+by/i, '');
  const order = orderText
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => {
      const [field, dir] = s.split(/\s+/);
      return { field: field.toLowerCase(), dir: /^desc$/i.test(dir) ? -1 : 1 };
    });

  const toks = tokenize(where);
  let i = 0;
  const peek = () => toks[i];
  const next = () => toks[i++];
  const unq = (v) => (/^["']/.test(v) ? v.slice(1, -1).replace(/\\(.)/g, '$1') : v);

  const value = () => {
    const v = next();
    if (v === undefined) throw new Error(t("Expected a value"));
    return unq(v);
  };
  const resolve = (raw) => {
    const v = String(raw);
    if (/^currentuser\(\)$/i.test(v)) return ctx.me || '';
    if (/^empty$/i.test(v) || /^null$/i.test(v)) return '';
    if (/^openSprints\(\)$/i.test(v)) return '__open';
    return v;
  };

  const cmp = (field) => {
    const getter = FIELD_GET[field.toLowerCase()];
    if (!getter) throw new Error(t("Unknown field “{{field}}”", { field }));
    let op = next();
    let negIn = false;
    if (/^not$/i.test(op)) {
      negIn = true;
      op = next();
    }
    if (/^is$/i.test(op)) {
      let v = next();
      if (/^not$/i.test(v)) {
        negIn = true;
        v = next();
      }
      const empty = (t) => {
        const g = getter(t);
        return Array.isArray(g) ? g.length === 0 : !g;
      };
      return (t) => (negIn ? !empty(t) : empty(t));
    }
    if (/^in$/i.test(op)) {
      if (next() !== '(') throw new Error(t("Expected ( after IN"));
      const vals = [];
      while (peek() !== ')') {
        if (peek() === undefined) throw new Error(t("Missing )"));
        if (peek() === ',') next();
        else {
          let vv = value();
          if (peek() === '(' && toks[i + 1] === ')') {
            vv += '()';
            i += 2;
          }
          vals.push(resolve(vv).toLowerCase());
        }
      }
      next();
      const test = (t) => {
        const g = getter(t);
        const arr = (Array.isArray(g) ? g : [g]).map((x) => String(x ?? '').toLowerCase());
        return vals.some((v) => arr.includes(v) || (v === '__open' && t.__sprintOpen));
      };
      return (t) => (negIn ? !test(t) : test(t));
    }
    const rawV = value();
    // functions written as name() are tokenised as a single word "name()" unless spaced; re-join if needed
    let v = rawV;
    if (peek() === '(' && toks[i + 1] === ')') {
      v = `${rawV}()`;
      i += 2;
    }
    v = resolve(v);
    const isDateField = ['created', 'updated', 'due', 'resolved'].includes(field.toLowerCase());
    return (t) => {
      const g = getter(t);
      const arr = Array.isArray(g) ? g : [g];
      const one = (x) => {
        if (isDateField) {
          const rel = relDate(String(v));
          const target = rel != null ? rel : new Date(v).getTime();
          const val = x ? new Date(x).getTime() : NaN;
          if (Number.isNaN(val)) return op === '!=' ;
          return { '=': val === target, '!=': val !== target, '<': val < target, '>': val > target, '<=': val <= target, '>=': val >= target }[op] || false;
        }
        const a = String(x ?? '').toLowerCase();
        const b = String(v).toLowerCase();
        if (op === '=') return v === '__open' ? !!t.__sprintOpen : a === b;
        if (op === '!=') return v === '__open' ? !t.__sprintOpen : a !== b;
        if (op === '~') return a.includes(b);
        const na = Number(x);
        const nb = Number(v);
        if (op === '<') return na < nb;
        if (op === '>') return na > nb;
        if (op === '<=') return na <= nb;
        if (op === '>=') return na >= nb;
        throw new Error(i18nT("Unknown operator “{{op}}”", { op }));
      };
      if (op === '!=') return arr.every(one);
      return arr.some(one);
    };
  };

  const primary = () => {
    const t = next();
    if (t === undefined) throw new Error(i18nT("Unexpected end of query"));
    if (t === '(') {
      const inner = orExpr();
      if (next() !== ')') throw new Error(i18nT("Missing )"));
      return inner;
    }
    if (/^not$/i.test(t)) {
      const inner = primary();
      return (x) => !inner(x);
    }
    return cmp(unq(t));
  };
  const andExpr = () => {
    let l = primary();
    while (/^and$/i.test(peek() || '')) {
      next();
      const r = primary();
      const a = l;
      l = (t) => a(t) && r(t);
    }
    return l;
  };
  function orExpr() {
    let l = andExpr();
    while (/^or$/i.test(peek() || '')) {
      next();
      const r = andExpr();
      const a = l;
      l = (t) => a(t) || r(t);
    }
    return l;
  }

  const test = toks.length ? orExpr() : () => true;
  if (i < toks.length) throw new Error(t("Unexpected “{{token}}”", { token: toks[i] }));
  return { test, order };
}

export function runJql(query, tasks, ctx) {
  const { test, order } = parseJql(query, ctx);
  const rows = tasks.filter(test);
  if (order.length) {
    rows.sort((a, b) => {
      for (const { field, dir } of order) {
        const g = FIELD_GET[field] || (() => '');
        const va = g(a);
        const vb = g(b);
        const na = typeof va === 'number' ? va : String(va ?? '').toLowerCase();
        const nb = typeof vb === 'number' ? vb : String(vb ?? '').toLowerCase();
        if (na < nb) return -dir;
        if (na > nb) return dir;
      }
      return 0;
    });
  }
  return rows;
}
